/**
 * EF-CREW-CALENDAR-VIEW-V1
 * EF-CREW-CALENDAR-UX-POLISH-V1
 * 
 * Lightweight, custom calendar grid optimized for mobile.
 * No external library dependencies - uses native JS date functions.
 * 
 * Features:
 * - 7-column grid (Mon-Sun)
 * - Month navigation (previous/next)
 * - Shift indicators (dots/badges) on days with shifts
 * - Day click modal showing shift details
 * - Visual states: accepted (green), pending (amber), rejected (red)
 * - Mobile-first: large tap areas, no horizontal scroll
 * - Empty day feedback
 * - Micro-interactions (tap feedback, smooth animations)
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useI18n } from "../../i18n/I18nProvider";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  MapPin,
  Briefcase,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  CalendarX,
  Check,
  X,
  Ban,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Day names (starting Monday)
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Month names
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// EF-CREW-CALENDAR-UX-POLISH-V1: Standardized status colors
const STATUS_COLORS = {
  accepted: {
    dot: "bg-emerald-500",
    badge: "border-emerald-500/50 text-emerald-600 bg-emerald-500/10",
    countBg: "bg-emerald-500/20 text-emerald-700",
    icon: CheckCircle2,
    label: "Accepted",
  },
  pending: {
    dot: "border-2 border-amber-500 bg-amber-500/20",
    badge: "border-amber-500/50 text-amber-600 bg-amber-500/10",
    countBg: "bg-amber-500/20 text-amber-700",
    icon: Clock,
    label: "Pending",
  },
  rejected: {
    dot: "bg-red-400/60",
    badge: "border-red-400/50 text-red-500 bg-red-500/10",
    countBg: "bg-red-400/20 text-red-600",
    icon: XCircle,
    label: "Rejected",
  },
  cancelled: {
    dot: "bg-zinc-400/60",
    badge: "border-zinc-400/50 text-zinc-500 bg-zinc-500/10",
    countBg: "bg-zinc-400/20 text-zinc-600",
    icon: Ban,
    label: "Cancelled",
  },
};

/**
 * Get the number of days in a month
 */
function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Get the day of week for the first day of month (0=Monday, 6=Sunday)
 */
function getFirstDayOfMonth(year, month) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

/**
 * Format date as YYYY-MM-DD
 */
function formatDateKey(year, month, day) {
  const m = String(month + 1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

/**
 * Parse date string to get YYYY-MM-DD key
 */
function parseDateToKey(dateStr) {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return formatDateKey(date.getFullYear(), date.getMonth(), date.getDate());
  } catch {
    return null;
  }
}

/**
 * Format time for display (HH:MM)
 */
function formatTime(timeStr) {
  if (!timeStr) return "TBD";
  if (typeof timeStr === "string" && timeStr.includes(":")) {
    return timeStr.substring(0, 5);
  }
  return timeStr;
}

/**
 * Format date for display in modal
 */
function formatDisplayDate(dateStr) {
  if (!dateStr) return "TBD";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format short date for empty state
 */
function formatShortDate(dateKey) {
  if (!dateKey) return "";
  try {
    const [year, month, day] = dateKey.split("-");
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  } catch {
    return dateKey;
  }
}

/**
 * EF-CREW-CALENDAR-UX-POLISH-V1: Standardized status badge
 */
function ShiftStatusBadge({ status }) {
  const config = STATUS_COLORS[status] || STATUS_COLORS.pending;
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={`${config.badge} text-xs`}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </Badge>
  );
}

/**
 * EF-CREW-CALENDAR-UX-POLISH-V1: Day cell with micro-interactions
 */
function DayCell({ 
  day, 
  dateKey,
  isCurrentMonth, 
  isToday, 
  shifts, 
  onClick 
}) {
  if (!day) {
    return <div className="aspect-square" />;
  }

  const hasShifts = shifts && shifts.length > 0;
  const shiftCount = shifts?.length || 0;
  
  // Determine dominant status for badge color
  const getDominantStatus = () => {
    if (!hasShifts) return null;
    const accepted = shifts.filter(s => s.status === "accepted").length;
    const pending = shifts.filter(s => s.status === "pending").length;
    if (accepted >= pending) return "accepted";
    if (pending > 0) return "pending";
    return "rejected";
  };
  
  const dominantStatus = getDominantStatus();

  return (
    <button
      onClick={() => onClick(shifts, dateKey, day)}
      className={`
        aspect-square flex flex-col items-center justify-center rounded-lg
        relative min-h-[44px] select-none
        transition-all duration-150 ease-out
        active:scale-95 active:bg-muted/80
        ${!isCurrentMonth ? "text-muted-foreground/40" : "text-foreground"}
        ${isToday ? "bg-primary/10 ring-2 ring-primary font-bold" : ""}
        ${hasShifts ? "cursor-pointer hover:bg-muted/60" : "cursor-pointer hover:bg-muted/30"}
      `}
      data-testid={`calendar-day-${day}`}
    >
      {/* Day number */}
      <span className={`text-sm leading-none ${isToday ? "text-primary" : ""}`}>
        {day}
      </span>
      
      {/* Shift indicators */}
      {hasShifts && (
        <div className="flex items-center justify-center gap-0.5 mt-1">
          {shiftCount <= 3 ? (
            // Show dots for 1-3 shifts
            shifts.slice(0, 3).map((shift, idx) => (
              <span
                key={idx}
                className={`
                  w-2 h-2 rounded-full transition-transform
                  ${STATUS_COLORS[shift.status]?.dot || STATUS_COLORS.pending.dot}
                `}
              />
            ))
          ) : (
            // Show count badge for 4+ shifts
            <span className={`
              text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none
              ${STATUS_COLORS[dominantStatus]?.countBg || "bg-muted text-muted-foreground"}
            `}>
              {shiftCount}
            </span>
          )}
        </div>
      )}
    </button>
  );
}

/**
 * EF-CREW-CALENDAR-UX-POLISH-V1: Improved shift detail card with better hierarchy
 */
function ShiftDetailCard({ shift, onAccept, onReject, actionInProgress }) {
  const statusConfig = STATUS_COLORS[shift.status] || STATUS_COLORS.pending;
  const isCancelled = shift.status === "cancelled";
  const isPending = shift.status === "pending";
  const canRespond = isPending && shift.can_respond !== false; // default true if undefined
  const isThisActionInProgress = actionInProgress === shift.shift_id;
  const isAnyActionInProgress = Boolean(actionInProgress);

  return (
    <div 
      className="p-4 rounded-xl border bg-card/50 hover:bg-muted/30 transition-colors"
      data-testid={`shift-detail-${shift.shift_id}`}
    >
      {/* Primary: Time (most important) */}
      <div className="flex items-center gap-2 text-base font-semibold mb-2">
        <Clock className="h-4 w-4 text-primary shrink-0" />
        <span>
          {formatTime(shift.start_time)}
          {shift.end_time && ` - ${formatTime(shift.end_time)}`}
        </span>
      </div>

      {/* Secondary: Role */}
      <div className="flex items-center gap-2 mb-2">
        <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="font-medium">{shift.role || "General"}</span>
        <ShiftStatusBadge status={shift.status} />
      </div>

      {/* Tertiary: Location */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <MapPin className="h-4 w-4 shrink-0" />
        <span className="truncate">{shift.location || "Location TBD"}</span>
      </div>

      {/* EF-SHIFT-MEETING-PLACE-PWA-V1: Meeting Place - prominently displayed */}
      {shift.meeting_place && (
        <div 
          className="mt-3 p-3 bg-primary/10 border border-primary/30 rounded-lg"
          data-testid={`meeting-place-${shift.shift_id}`}
        >
          <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1 flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            Meeting Place
          </p>
          <p className="text-sm text-foreground whitespace-pre-wrap break-words">
            {shift.meeting_place}
          </p>
          {/* Open in Maps link */}
          {shift.meeting_place.length > 10 && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shift.meeting_place)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-xs text-primary hover:underline font-medium"
              data-testid={`open-maps-${shift.shift_id}`}
            >
              <MapPin className="h-3 w-3" />
              Open in Maps
            </a>
          )}
        </div>
      )}

      {/* Project name if available */}
      {shift.project_name && (
        <div className="text-xs text-muted-foreground/80 mt-3 pt-2 border-t border-border/50">
          {shift.project_name}
        </div>
      )}

      {/* EVENTSFLO-CREW-PWA-LINK-SEND-AND-PENDING-ACTIONS-RESTORE-V1
          Accept / Reject for pending requests. Cancelled requests show a
          quiet notice instead of action buttons. */}
      {canRespond && onAccept && onReject && (
        <div className="flex gap-2 mt-4 pt-3 border-t border-border/50">
          <Button
            size="sm"
            variant="default"
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            onClick={() => onAccept(shift.shift_id)}
            disabled={isAnyActionInProgress}
            data-testid={`calendar-accept-btn-${shift.shift_id}`}
          >
            {isThisActionInProgress ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Check className="h-4 w-4 mr-1" />
            )}
            Accept
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 border-red-500/50 text-red-500 hover:bg-red-500/10"
            onClick={() => onReject(shift.shift_id)}
            disabled={isAnyActionInProgress}
            data-testid={`calendar-reject-btn-${shift.shift_id}`}
          >
            {isThisActionInProgress ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <X className="h-4 w-4 mr-1" />
            )}
            Reject
          </Button>
        </div>
      )}

      {isCancelled && (
        <div
          className="mt-4 pt-3 border-t border-border/50 flex items-center gap-2 text-xs text-muted-foreground"
          data-testid={`calendar-cancelled-notice-${shift.shift_id}`}
        >
          <Ban className="h-3.5 w-3.5" />
          <span>This shift was cancelled by admin.</span>
        </div>
      )}
    </div>
  );
}

/**
 * EF-CREW-CALENDAR-UX-POLISH-V1: Improved day detail modal with empty state
 */
function DayDetailModal({ isOpen, onClose, date, dateKey, shifts, onAccept, onReject, actionInProgress }) {
  const { t } = useI18n();
  const hasShifts = shifts && shifts.length > 0;
  const shiftCount = shifts?.length || 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-sm mx-4 p-0 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200" 
        data-testid="day-detail-modal"
      >
        {/* Header with shift count */}
        <DialogHeader className="p-4 pb-3 border-b bg-muted/30">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Calendar className="h-5 w-5 text-primary" />
            <span>{date ? formatDisplayDate(date) : formatShortDate(dateKey)}</span>
          </DialogTitle>
          <DialogDescription className="text-sm font-medium text-foreground mt-1">
            {hasShifts 
              ? `${shiftCount} shift${shiftCount !== 1 ? "s" : ""} scheduled`
              : "Check your schedule for this day"
            }
          </DialogDescription>
        </DialogHeader>
        
        {/* Content */}
        <div className="p-4">
          {hasShifts ? (
            <div className="space-y-3 max-h-[55vh] overflow-y-auto -mr-2 pr-2">
              {shifts.map((shift, idx) => (
                <ShiftDetailCard
                  key={shift.shift_id || idx}
                  shift={shift}
                  onAccept={onAccept}
                  onReject={onReject}
                  actionInProgress={actionInProgress}
                />
              ))}
            </div>
          ) : (
            /* EF-CREW-CALENDAR-UX-POLISH-V1: Empty day state */
            <div className="text-center py-8" data-testid="empty-day-state">
              <CalendarX className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground font-medium">{t("crewPortal.calendarView.noShiftsScheduledDay")}</p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                {t("crewPortal.calendarView.noShiftsScheduledDayHint")}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * EF-CREW-CALENDAR-UX-POLISH-V1: Enhanced calendar header with prominent Today button
 */
function CalendarHeader({ year, month, isCurrentMonth, onPrevMonth, onNextMonth, onToday }) {
  return (
    <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm pb-3 -mx-4 px-4 -mt-2 pt-2">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          onClick={onPrevMonth}
          className="h-10 w-10 rounded-full hover:bg-muted active:scale-95 transition-transform"
          data-testid="prev-month-btn"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">
            {MONTH_NAMES[month]} {year}
          </h2>
          {!isCurrentMonth && (
            <Button
              variant="default"
              size="sm"
              onClick={onToday}
              className="text-xs h-7 px-3 rounded-full font-medium shadow-sm"
              data-testid="today-btn"
            >
              Today
            </Button>
          )}
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={onNextMonth}
          className="h-10 w-10 rounded-full hover:bg-muted active:scale-95 transition-transform"
          data-testid="next-month-btn"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Loading state
 */
function LoadingState() {
  return (
    <div className="flex items-center justify-center py-16" data-testid="calendar-loading">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

/**
 * Error state
 */
function ErrorState({ error, onRetry }) {
  const { t } = useI18n();
  return (
    <div className="text-center py-12" data-testid="calendar-error">
      <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
      <p className="text-destructive font-medium">{t("crewPortal.calendarView.loadFailed")}</p>
      <p className="text-sm text-muted-foreground mt-2">{error}</p>
      <Button variant="outline" size="sm" onClick={onRetry} className="mt-4">
        <RefreshCw className="h-4 w-4 mr-2" />
        {t("crewPortal.calendarView.retry")}
      </Button>
    </div>
  );
}

/**
 * EF-CREW-CALENDAR-UX-POLISH-V1: Improved calendar legend
 */
function CalendarLegend() {
  const { t } = useI18n();
  return (
    <div className="flex items-center justify-center gap-5 text-xs text-muted-foreground mt-4 py-2">
      <div className="flex items-center gap-1.5">
        <span className={`w-2.5 h-2.5 rounded-full ${STATUS_COLORS.accepted.dot}`} />
        <span>{t("crewPortal.shiftList.statusAccepted")}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className={`w-2.5 h-2.5 rounded-full ${STATUS_COLORS.pending.dot}`} />
        <span>{t("crewPortal.shiftList.filterPending")}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className={`w-2.5 h-2.5 rounded-full ${STATUS_COLORS.rejected.dot}`} />
        <span>{t("crewPortal.shiftList.statusRejected")}</span>
      </div>
    </div>
  );
}

/**
 * Main CrewCalendarView component
 */
export function CrewCalendarView() {
  const { t } = useI18n();
  // Current displayed month/year
  const today = new Date();
  const [displayMonth, setDisplayMonth] = useState(today.getMonth());
  const [displayYear, setDisplayYear] = useState(today.getFullYear());
  
  // Shifts data - fetched once, reused across month navigation
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  // EVENTSFLO-CREW-PWA-LINK-SEND-AND-PENDING-ACTIONS-RESTORE-V1
  const [actionInProgress, setActionInProgress] = useState(null);

  // Today's date key
  const todayKey = formatDateKey(today.getFullYear(), today.getMonth(), today.getDate());
  
  // Check if viewing current month
  const isCurrentMonth = displayMonth === today.getMonth() && displayYear === today.getFullYear();

  /**
   * Fetch shifts from API - only on mount, data reused for all months
   * EF-CREW-CALENDAR-UX-POLISH-V1: Performance - no re-fetch per month change
   */
  const fetchShifts = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("eventsflo_auth_token");
      if (!token) {
        throw new Error("Authentication required");
      }

      // Include past shifts for calendar view
      const response = await fetch(`${API_URL}/api/crew/shifts?include_past=true`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Failed to fetch shifts (${response.status})`);
      }

      const data = await response.json();
      setShifts(data.shifts || []);
    } catch (err) {
      console.error("Error fetching shifts:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load only - no refetch on month change
  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  /**
   * Group shifts by date (YYYY-MM-DD) - memoized for performance
   */
  const shiftsByDate = useMemo(() => {
    const grouped = {};
    for (const shift of shifts) {
      const key = parseDateToKey(shift.date);
      if (key) {
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(shift);
      }
    }
    return grouped;
  }, [shifts]);

  /**
   * Generate calendar grid - memoized, uses cached shiftsByDate
   */
  const calendarGrid = useMemo(() => {
    const daysInMonth = getDaysInMonth(displayYear, displayMonth);
    const firstDay = getFirstDayOfMonth(displayYear, displayMonth);
    
    const grid = [];
    let dayCounter = 1;
    
    const totalCells = Math.ceil((daysInMonth + firstDay) / 7) * 7;
    const rows = totalCells / 7;
    
    for (let row = 0; row < rows; row++) {
      const week = [];
      for (let col = 0; col < 7; col++) {
        const cellIndex = row * 7 + col;
        
        if (cellIndex < firstDay || dayCounter > daysInMonth) {
          week.push({ day: null, dateKey: null });
        } else {
          const dateKey = formatDateKey(displayYear, displayMonth, dayCounter);
          week.push({
            day: dayCounter,
            dateKey,
            isToday: dateKey === todayKey,
            shifts: shiftsByDate[dateKey] || [],
          });
          dayCounter++;
        }
      }
      grid.push(week);
    }
    
    return grid;
  }, [displayYear, displayMonth, shiftsByDate, todayKey]);

  /**
   * Month navigation handlers - no data refetch
   */
  const handlePrevMonth = useCallback(() => {
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear(y => y - 1);
    } else {
      setDisplayMonth(m => m - 1);
    }
  }, [displayMonth]);

  const handleNextMonth = useCallback(() => {
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(y => y + 1);
    } else {
      setDisplayMonth(m => m + 1);
    }
  }, [displayMonth]);

  const handleToday = useCallback(() => {
    const now = new Date();
    setDisplayMonth(now.getMonth());
    setDisplayYear(now.getFullYear());
  }, []);

  /**
   * Handle day click - shows modal for any day (empty or with shifts)
   * EF-CREW-CALENDAR-UX-POLISH-V1: Empty day UX
   */
  const handleDayClick = useCallback((dayShifts, dateKey, day) => {
    // Get full date for display
    const fullDate = dayShifts?.length > 0 ? dayShifts[0].date : null;
    setSelectedDate(fullDate);
    setSelectedDateKey(dateKey);
    setSelectedShifts(dayShifts || []);
    setModalOpen(true);
  }, []);

  /**
   * Close modal
   */
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    // Delay clearing data for smooth animation
    setTimeout(() => {
      setSelectedDate(null);
      setSelectedDateKey(null);
      setSelectedShifts([]);
    }, 200);
  }, []);

  /**
   * EVENTSFLO-CREW-PWA-LINK-SEND-AND-PENDING-ACTIONS-RESTORE-V1
   * Accept/Reject pending shifts directly from the calendar day modal —
   * mirrors the pattern in CrewShiftList so behaviour stays consistent.
   * Optimistically updates both the modal's selectedShifts list AND the
   * cached shifts array so the calendar dot recolors immediately.
   */
  const applyShiftStatusUpdate = useCallback((shiftId, patch) => {
    setShifts((prev) => prev.map((s) => (s.shift_id === shiftId ? { ...s, ...patch } : s)));
    setSelectedShifts((prev) => prev.map((s) => (s.shift_id === shiftId ? { ...s, ...patch } : s)));
  }, []);

  const respondToShift = useCallback(
    async (shiftId, action) => {
      if (!shiftId || actionInProgress) return;
      setActionInProgress(shiftId);
      try {
        const token = localStorage.getItem("eventsflo_auth_token");
        if (!token) throw new Error("Authentication required");
        const url = `${API_URL}/api/crew/shifts/${shiftId}/${action}`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: action === "reject" ? JSON.stringify({}) : undefined,
        });

        if (response.status === 410) {
          // Admin cancelled the shift — sync UI to reflect that.
          applyShiftStatusUpdate(shiftId, { status: "cancelled", can_respond: false });
          toast.error(t("crewPortal.shiftList.withdrawCancelledByAdmin"));
          return;
        }
        if (response.status === 409) {
          // Already responded — refetch authoritative state.
          toast.info(t("calendarView.alreadyResponded"));
          await fetchShifts();
          return;
        }
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.detail || `Failed to ${action} shift`);
        }
        const result = await response.json();
        const newStatus = action === "accept" ? "accepted" : "rejected";
        applyShiftStatusUpdate(shiftId, {
          status: newStatus,
          can_respond: false,
          responded_at: result.responded_at,
        });
        toast.success(action === "accept" ? "Shift accepted!" : "Shift rejected");
      } catch (err) {
        console.error(`${action} error:`, err);
        toast.error(err.message || `Failed to ${action} shift`);
      } finally {
        setActionInProgress(null);
      }
    },
    [actionInProgress, applyShiftStatusUpdate, fetchShifts],
  );

  const handleAccept = useCallback((shiftId) => respondToShift(shiftId, "accept"), [respondToShift]);
  const handleReject = useCallback((shiftId) => respondToShift(shiftId, "reject"), [respondToShift]);

  // Loading state
  if (loading) {
    return <LoadingState />;
  }

  // Error state
  if (error) {
    return <ErrorState error={error} onRetry={fetchShifts} />;
  }

  return (
    <div data-testid="crew-calendar-view">
      {/* Calendar Header - sticky with prominent Today button */}
      <CalendarHeader
        year={displayYear}
        month={displayMonth}
        isCurrentMonth={isCurrentMonth}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
      />

      {/* Calendar Grid */}
      <Card className="overflow-hidden">
        <CardContent className="p-2 sm:p-3">
          {/* Day names header */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAY_NAMES.map(day => (
              <div 
                key={day} 
                className="text-center text-xs font-medium text-muted-foreground py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid with micro-interactions */}
          <div className="grid grid-cols-7 gap-1">
            {calendarGrid.map((week, weekIdx) =>
              week.map((cell, dayIdx) => (
                <DayCell
                  key={`${weekIdx}-${dayIdx}`}
                  day={cell.day}
                  dateKey={cell.dateKey}
                  isCurrentMonth={cell.day !== null}
                  isToday={cell.isToday}
                  shifts={cell.shifts}
                  onClick={handleDayClick}
                />
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <CalendarLegend />

      {/* Shifts summary */}
      {shifts.length > 0 && (
        <div className="text-center text-sm text-muted-foreground mt-2">
          {shifts.length} total shift{shifts.length !== 1 ? "s" : ""} loaded
        </div>
      )}

      {/* Day Detail Modal with empty state support */}
      <DayDetailModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        date={selectedDate}
        dateKey={selectedDateKey}
        shifts={selectedShifts}
        onAccept={handleAccept}
        onReject={handleReject}
        actionInProgress={actionInProgress}
      />
    </div>
  );
}

export default CrewCalendarView;
